import { useState } from 'react';
import { IUnitConfiguration } from '../../../../Models/IUnitConfiguration';
import { IBlindTest } from '../../../../Models/IBlindTest';
import { getLocalStorageItem } from '../../../../Services/Global';
import { getPageKeyName } from '../../../../Utils/UnitConfigurationHelper';
import { Constants } from '../../../../Models/Constants';

// Define component props interface
export interface BlindTestListProps {
  unitConfiguration?: IUnitConfiguration;
  blindTests?: IBlindTest[];
}

// Define column configuration interface
export interface ColumnConfig {
  id: string;
  label: string;
  width: ColumnWidth;
  align: 'left' | 'right' | 'center';
  format: (value: any, row: any) => React.ReactNode;
  sortable?: boolean;
}

// Define column width presets
export interface ColumnWidth {
  minWidth: number;
  maxWidth: number;
}

// Define column width constants
export const COLUMN_WIDTHS = {
  row: { minWidth: 60, maxWidth: 80 },
  source: { minWidth: 80, maxWidth: 100 },
  modelId: { minWidth: 100, maxWidth: 150 },
  mediaType: { minWidth: 100, maxWidth: 100 },
  date: { minWidth: 100, maxWidth: 150 },
  temperature: { minWidth: 150, maxWidth: 180 },
  flowrate: { minWidth: 150, maxWidth: 180 },
  pressure: { minWidth: 150, maxWidth: 180 },
  comment: { minWidth: 80, maxWidth: 120 },
  default: { minWidth: 150, maxWidth: 180 }
};

export interface BlindTestListState {
  // Unit configuration and data states
  unitInputConfiguration: IUnitConfiguration;
  setUnitInputConfiguration: (config: IUnitConfiguration) => void;
  
  // Pagination and data states
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  pageCount: number;
  setPageCount: (count: number) => void;
  currentData: any[];
  setCurrentData: (data: any[]) => void;
  currentFilter: string;
  setCurrentFilter: (filter: string) => void;
  configurations: IUnitConfiguration[];
  setConfigurations: (configs: IUnitConfiguration[]) => void;
  columns: ColumnConfig[];
  setColumns: (columns: ColumnConfig[]) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  rowsPerPage: number;
  setRowsPerPage: (rows: number) => void;
  
  // Filter states
  filterValue: string;
  setFilterValue: (value: string) => void;
  filterColumn: string;
  setFilterColumn: (column: string) => void;
  
  // Sorting states
  orderBy: string;
  setOrderBy: (orderBy: string) => void;
  order: 'asc' | 'desc';
  setOrder: (order: 'asc' | 'desc') => void;
  
  // Column menu states
  columnMenuAnchor: null | HTMLElement;
  setColumnMenuAnchor: (anchor: null | HTMLElement) => void;
  activeColumn: string;
  setActiveColumn: (column: string) => void;
  hiddenColumns: string[];
  setHiddenColumns: (columns: string[]) => void;
  columnManagerOpen: boolean;
  setColumnManagerOpen: (open: boolean) => void;
  
  // Preferred units
  preferredFlowrateUnits: string;
  setPreferredFlowrateUnits: (units: string) => void;
  
  // Page key name for local storage
  pageKeyName: string;
}

/**
 * Custom hook for BlindTestList state management
 */
export const useBlindTestListState = (
  propUnitConfiguration?: IUnitConfiguration, 
  state?: { positionSummary: any }
): BlindTestListState => {
  
  // Initialize unit configuration
  const initialUnitConfig = propUnitConfiguration || (state?.positionSummary ? {
    industry: state.positionSummary.industry,
    subIndustry: state.positionSummary.subIndustry,
    application: state.positionSummary.application,
    subApplication: state.positionSummary.subApplication,
    processStage: state.positionSummary.processStage,
    productLine: state.positionSummary.productLine,
    alfaLavalPosition: state.positionSummary.alfaLavalPosition,
    breakdownOfProcessStage: '',
    coldMediaType: '',
    hotMediaType: '',
    inputs: [],
    nonUsedProperties: []
  } : {} as IUnitConfiguration);

  const [unitInputConfiguration, setUnitInputConfiguration] = useState<IUnitConfiguration>(initialUnitConfig);
  
  // Calculate page key name for local storage
  const pageKeyName = unitInputConfiguration ? getPageKeyName(unitInputConfiguration, Constants.UnitResultListPage) : '';
  
  // Pagination and data states
  const [currentPage, setCurrentPage] = useState(Number(getLocalStorageItem(pageKeyName) ?? 0));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageCount, setPageCount] = useState<number>(0);
  const [currentData, setCurrentData] = useState<any[]>([]);
  const [currentFilter, setCurrentFilter] = useState(getLocalStorageItem(`${pageKeyName}_currentFilter`) ?? '');
  const [configurations, setConfigurations] = useState<IUnitConfiguration[]>([]);
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);

  // Filter states
  const [filterValue, setFilterValue] = useState<string>('');
  const [filterColumn, setFilterColumn] = useState<string>('');

  // Sorting states
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Column menu states
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string>('');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnManagerOpen, setColumnManagerOpen] = useState<boolean>(false);

  // Preferred units
  const [preferredFlowrateUnits, setPreferredFlowrateUnits] = useState<string>('');

  return {
    unitInputConfiguration,
    setUnitInputConfiguration,
    currentPage,
    setCurrentPage,
    isLoading,
    setIsLoading,
    pageCount,
    setPageCount,
    currentData,
    setCurrentData,
    currentFilter,
    setCurrentFilter,
    configurations,
    setConfigurations,
    columns,
    setColumns,
    showFilters,
    setShowFilters,
    rowsPerPage,
    setRowsPerPage,
    filterValue,
    setFilterValue,
    filterColumn,
    setFilterColumn,
    orderBy,
    setOrderBy,
    order,
    setOrder,
    columnMenuAnchor,
    setColumnMenuAnchor,
    activeColumn,
    setActiveColumn,
    hiddenColumns,
    setHiddenColumns,
    columnManagerOpen,
    setColumnManagerOpen,
    preferredFlowrateUnits,
    setPreferredFlowrateUnits,
    pageKeyName
  };
};
